import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useV2GetGovernanceByIdQuery } from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  AdminFormPage,
  saveErrorToast,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { GovernanceForm } from "../GovernanceForm/GovernanceForm";
import { useGovernanceMutations } from "../api/useGovernanceMutations";
import type { GovernanceFormValues } from "../types/Governance";
import { rateRulesFromApi } from "@components/GovernanceEffort/rateRules";
import { governanceVariablesFromApi } from "../api/governanceVariables";

const FORM_ID = "governance-form";

export const Edit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // refetchOnMountOrArgChange so re-entering Edit after a save re-requests
  // the record rather than trusting the (never-invalidated) cache.
  const { data, isLoading } = useV2GetGovernanceByIdQuery(
    { id: id!, include: ["resource", "phase", "service-category"] },
    { refetchOnMountOrArgChange: true }
  );
  const { update } = useGovernanceMutations();
  const [submitting, setSubmitting] = useState(false);
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<GovernanceFormValues>();

  const initial = useMemo<GovernanceFormValues | null>(() => {
    if (!data?.data) return null;
    const record: any = data.data;
    return {
      name: record.attributes.name ?? "",
      resourceId: record.relationships?.resource?.data?.id ?? null,
      required: !!record.attributes.required,
      calculationType: record.attributes["calculation-type"] ?? "fixed_hours",
      fixedHours: record.attributes["fixed-hours"] ?? null,
      rate: record.attributes.rate ?? null,
      // Decimal columns serialize as strings.
      minimumHours:
        record.attributes["minimum-hours"] == null
          ? null
          : Number(record.attributes["minimum-hours"]),
      rateRules: rateRulesFromApi(record.attributes["rate-rules"]),
      projectVariables: governanceVariablesFromApi(
        record.attributes["project-variables"]
      ),
      filterType: record.attributes["filter-type"] ?? "all_services",
      filterId: record.attributes["filter-id"] ?? null,
      allocationMethod: record.attributes["allocation-method"] ?? null,
      phaseId: record.relationships?.phase?.data?.id ?? null,
      sectionId: record.relationships?.["service-category"]?.data?.id ?? null,
      assignEffortToService: !!record.attributes["assign-effort-to-service"],
    };
  }, [data]);

  if (isLoading || !initial) return <ScopeStackSpinner />;

  const recordName = (data?.data as any)?.attributes?.name || "Governance";

  const handleSubmit = async (values: GovernanceFormValues) => {
    setSubmitting(true);
    try {
      await update(id!, values);
      markSaved(values, "Governance updated.");
    } catch (err) {
      console.error("Failed to update governance", err);
      setToast(saveErrorToast(err, "governance"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title={`Edit ${recordName}`}
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        isSubmitting={submitting}
      >
        <GovernanceForm
          key={formKey}
          id={FORM_ID}
          initialValues={savedInitial ?? initial}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
